import React from 'react';
import { isTeamLeaderEmail } from '../../config/commissionPlans';
import { priceRangeText, propertyTypesText, bedsText, bathsText, sqftRangeText } from './homeSearch';

// Merge fields for emails, texts and call scripts. Syntax: {{first_name}} or with a fallback
// {{interested_city|Southwest Florida}}. Unknown tokens (like {{unsubscribe_url}}) are left alone.
export const MERGE_FIELDS = [
  ['first_name', 'First name'],
  ['last_name', 'Last name'],
  ['full_name', 'Full name'],
  ['phone', 'Phone'],
  ['email', 'Email'],
  ['source', 'Lead source'],
  ['tags', 'Tags'],
  ['notes', 'Notes'],
  ['interested_city', 'Interested city'],
  ['interested_development', 'Interested development'],
  ['price_range', 'Price range'],
  ['property_type', 'Property type'],
  ['min_beds', 'Min bedrooms'],
  ['min_baths', 'Min bathrooms'],
  ['sqft_range', 'Sq ft range'],
  ['agent_name', 'Agent name'],
  ['agent_first_name', 'Agent first name'],
  ['agent_last_name', 'Agent last name'],
  ['agent_phone', 'Agent phone'],
  ['agent_email', 'Agent email'],
  ['agent_photo_url', 'Agent photo URL'],
  ['co_agent_block', 'Assigned-agent intro (team leader emails)'],
];
// Appointment fields: only filled when the contact carries _appt (confirmation/reminder messages).
export const APPT_FIELDS = [['appt_date', 'Appointment date'], ['appt_time', 'Appointment time'], ['appt_type', 'Appointment type'], ['appt_location', 'Location'], ['zoom_link', 'Zoom link']];
export const SAMPLE_CONTACT = { firstName: 'Jordan', lastName: 'Sample', interestedCity: 'Naples', interestedDevelopment: 'Esplanade', priceMin: 400000, priceMax: 650000, propertyTypes: ['Villa'], minBeds: 3, minBaths: 2, sqftMin: 1800, sqftMax: 2500 };

const splitFirst = (c) => c.firstName || String(c.name || '').trim().split(/\s+/)[0] || '';
const splitLast = (c) => c.lastName || String(c.name || '').trim().split(/\s+/).slice(1).join(' ') || '';
export const agentDisplayName = (a = {}) => [a.firstName, a.lastName].filter(Boolean).join(' ') || a.displayName || a.name || '';

const agentFirst = (a = {}) => a.firstName || String(a.displayName || a.name || '').trim().split(/\s+/)[0] || '';
const agentLast = (a = {}) => a.lastName || String(a.displayName || a.name || '').trim().split(/\s+/).slice(1).join(' ') || '';
export const agentPhotoUrl = (a = {}) => a.profilePhotoUrl || a.profile?.profilePhotoUrl || a.photoURL || '';
const agentPhone = (a = {}) => a.phone || a.profile?.phone || '';
// The assigned agent is "someone else on the team" unless they are the team leader (Kelly) or missing.
export const isCoAgent = (a = {}) => !!(a && agentDisplayName(a)) && !isTeamLeaderEmail(a.email);
const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
// Raw HTML block for team-leader emails: introduces the assigned agent (photo if on file, else name + contact).
// Empty when the lead is assigned to the team leader herself, or nobody is assigned.
export const coAgentBlockHtml = (a = {}) => {
  if (!isCoAgent(a)) return '';
  const name = esc(agentDisplayName(a)); const first = esc(agentFirst(a) || agentDisplayName(a));
  const photo = agentPhotoUrl(a); const phone = agentPhone(a); const email = a.email || '';
  const contact = [phone && `📞 <a href="tel:${esc(phone.replace(/[^0-9+]/g, ''))}">${esc(phone)}</a>`, email && `✉️ <a href="mailto:${esc(email)}">${esc(email)}</a>`].filter(Boolean).join(' &nbsp; ');
  return `<table role="presentation" cellpadding="0" cellspacing="0" style="margin:14px 0;border:1px solid #ddd;border-radius:6px;width:100%;max-width:600px"><tr>${photo ? `<td style="padding:12px;width:96px;vertical-align:top"><img src="${esc(photo)}" alt="${name}" width="88" height="88" style="width:88px;height:88px;border-radius:50%;object-fit:cover;display:block"/></td>` : ''}<td style="padding:12px;vertical-align:middle;font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.4"><strong>You’ll be working with ${name} on my team.</strong> ${first} will be reaching out to you personally to help with your search, so keep an eye out!${contact ? `<br/>${contact}` : ''}</td></tr></table>`;
};

const valueFor = (key, c = {}, agent = {}) => ({
  first_name: splitFirst(c),
  last_name: splitLast(c),
  full_name: [splitFirst(c), splitLast(c)].filter(Boolean).join(' '),
  phone: c.phone || '',
  email: c.email || '',
  source: c.source || '',
  tags: [...(c.tags || []), ...(c.hashtags || [])].filter(Boolean).map((t) => `#${String(t).replace(/^#+/, '')}`).join(' '),
  notes: String((typeof c.notes === 'string' ? c.notes : '') || c.note || '').trim().slice(0, 200),
  interested_city: c.interestedCity || '',
  interested_development: c.interestedDevelopment || '',
  price_range: priceRangeText(c),
  property_type: propertyTypesText(c),
  min_beds: bedsText(c),
  min_baths: bathsText(c),
  sqft_range: sqftRangeText(c),
  agent_name: agentDisplayName(agent),
  agent_first_name: agentFirst(agent),
  agent_last_name: agentLast(agent),
  agent_phone: agentPhone(agent),
  agent_email: agent.email || '',
  agent_photo_url: agentPhotoUrl(agent),
  appt_date: c._appt?.date || undefined,
  appt_time: c._appt?.time || undefined,
  appt_type: c._appt?.type || undefined,
  appt_location: c._appt ? c._appt.location || '' : undefined,
  zoom_link: c._appt ? c._appt.zoomLink || '' : undefined,
}[key]);

const escHtml = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

// html: true escapes the inserted values (for email HTML).
export const applyMerge = (text, contact, agent, { html = false } = {}) => String(text || '').replace(
  /\{\{\s*([a-z_]+)\s*(?:\|([^}]*))?\}\}/g,
  (whole, key, fallback) => {
    if (key === 'co_agent_block') return html ? coAgentBlockHtml(agent || {}) : (isCoAgent(agent || {}) ? `You'll be working with ${agentDisplayName(agent)} on my team; they'll be reaching out to you personally.` : '');
    const v = valueFor(key, contact, agent || {});
    if (v === undefined) return whole;
    const out = String(v).trim() || (fallback || '').trim();
    return html ? escHtml(out) : out;
  },
);

// Inserts a token at the cursor of an input/textarea element; returns the new value.
export const insertAtCursor = (el, value, token) => {
  const v = value || '';
  if (!el || typeof el.selectionStart !== 'number') return v + token;
  const start = el.selectionStart; const end = el.selectionEnd;
  const next = v.slice(0, start) + token + v.slice(end);
  requestAnimationFrame(() => { try { el.focus(); el.setSelectionRange(start + token.length, start + token.length); } catch (e) { /* ignore */ } });
  return next;
};

export function InsertFieldMenu({ onInsert, style }) {
  return (
    <select aria-label="Insert field" value="" style={style} onMouseDown={(e) => e.stopPropagation()}
      onChange={(e) => { if (e.target.value) onInsert(`{{${e.target.value}}}`); }}>
      <option value="">Insert field…</option>
      {MERGE_FIELDS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
    </select>
  );
}
